import { uid } from 'uid';
import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { dispatchEvent } from '@fil-global/shared';

import componentStyles from './fil-switch.styles.js';

// TODO remove this
import '@fil-global/fil-icon';

/**
 * @tag fil-switch
 * @property {boolean}[checked] Checked state of the component
 * @property {boolean}[disabled] Disabled state of the component
 * @property {boolean}[first] Whether this is the first component of switch-group
 * @property {boolean}[last] Whether this is the last component of switch-group
 * @property {string}[icon] Icon used in the component
 * @property {string}[label] Label of the component
 * @property {string}[name] Name of the component
 * @property {'standard'|'small'}[size] Size of the component
 * @property {string}[value] Value of the component
 * @fires change.gds.switch (event: Event) => void
 */
export class FilSwitch extends LitElement {
  static properties = {
    checked: { type: Boolean },
    disabled: { type: Boolean },
    first: { type: Boolean },
    icon: { type: String },
    label: { type: String },
    last: { type: Boolean },
    name: { type: String },
    size: { type: String }, // 'small' | 'standard'
    value: { type: String },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.checked = false;
    this.disabled = false;
    this.first = false;
    this.last = false;
    this.size = 'standard';

    this.uid = uid();
  }

  onChange() {
    this.checked = true;
  }

  onKeypress(event) {
    // check for key 'space'
    if (event.keyCode === 32) {
      event.preventDefault();

      if (!this.disabled) {
        this.onChange();
      }
    }
  }

  update(changedProperties) {
    super.update(changedProperties);

    if (changedProperties.has('checked')) {
      if (this.checked) {
        if (changedProperties.get('checked') !== undefined && !changedProperties.get('checked')) {
          // Event should be dispatched only inside live cycle method not in change handler.
          // State changes by user interaction will always trigger a change event where state changes
          // programmatically (by script) won't do. This is intended as you can see in the
          // documentation of Lit (https://lit.dev/docs/components/events/#when-to-dispatch-an-event).
          // Unfortunately we are in the need to dispatch the change event regardless if the state
          // change is done by user interaction or programmatically. We solve it by moving the dispatch
          // into the live cycle method as this one will be called in both cases.
          this.onChange();
          const { name, value } = this.renderRoot.querySelector('input');
          dispatchEvent(this, 'change.gds.switch', { name, value });
        }
      }
    }
  }

  render() {
    const classes = {
      'fil-switch--first': this.first,
      'fil-switch--last': this.last,
      'fil-switch--icon': this.icon,
      [`fil-switch--${this.size}`]: this.size !== 'standard',
    };

    return html`
      <div class="fil-switch${classMap(classes)}">
        <input
          aria-checked=${this.checked}
          aria-labelledby="label_${this.uid}"
          @change=${this.onChange}
          .checked=${this.checked}
          class="fil-switch__field"
          ?disabled=${this.disabled}
          id="${this.uid}"
          name="${this.name}"
          type="radio"
          value=${this.value}
        />
        <label
          class="fil-switch__label"
          for="${this.uid}"
          id="label_${this.uid}"
          @keypress=${this.onKeypress}
          aria-label="${this.name}"
          tabindex="0"
        >
          ${this.icon && html`<fil-icon size="20" icon="${this.icon}"></fil-icon>`}
          ${this.label && html`<span>${this.label}</span>`}</label
        >
      </div>
    `;
  }
}

export default FilSwitch;
